import { useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import { saveAs } from 'file-saver';
import { useDispatch } from 'react-redux';
import CardListTable from 'src/components/cards/CardListTable';
import TableColumnVisibilityMenu, { useTableColumnPreferences } from 'src/common/TableColumnVisibilityMenu';
import { cardTableHeaders } from 'src/utils/tableHeaders';
import NewCard from 'src/components/cards/NewCard';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { setErrorInfo } from 'src/redux/modalSlice';
import { getCardDetails } from 'src/redux/cardSlice';
import { BASEAPI } from 'src/api/api';
import { DATE_FORMAT } from 'src/utils/constants';
import moment from 'moment';
import API_URL from 'src/api/apiConstants';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';
import ListRefreshButton from 'src/common/ListRefreshButton';
import ColumnSearch from 'src/common/ColumnSearch';
import { formatCardDetails } from 'src/components/cards/formModel/cardFormUtils';
import ExportColumnSelector from 'src/common/ExportColumnSelector';
import { EXPORT_COLUMNS } from 'src/utils/exportColumns';

const Cards = ({ location }) => {
  const {
    search: persistedSearch,
    site: persistedSite,
    setSearch: persistSearch,
    setSite: persistSite
  } = usePersistentListFilters();
  const dispatch = useDispatch();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(persistedSite);

  const [openModal, setOpenModal] = useState(false);
  const [searchText, setSearchText] = useState(persistedSearch);
  const [searchFilters, setSearchFilters] = useState({});
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [showExportColumns, setShowExportColumns] = useState(false);
  const [exportColumns, setExportColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingCard, setEditingCard] = useState(null);
  const cardColumnPreferences = useTableColumnPreferences('cards', cardTableHeaders);

  const handleSearch = (value) => {
    setSearchText(value);
    persistSearch(value);
  };

  const handleSiteChange = (value) => {
    setSiteFilter(value);
    persistSite(value);
  };

  const handleEditCard = async (cardId) => {
    try {
      const cardDetails = await dispatch(getCardDetails(cardId)).unwrap();
      setEditingCard(formatCardDetails(cardDetails));
    } catch (error) {
      dispatch(setErrorInfo({
        errorMsg: error?.error_message || 'Unable to load card details.'
      }));
    }
  };

  const handleCloseEditCard = () => setEditingCard(null);

  const handleExport = () => {
    setShowExportConfirm(false);
    setIsLoading(true);
    const columns = exportColumns.length
      ? `&columns=${encodeURIComponent(exportColumns.join(','))}`
      : '';
    BASEAPI.get(`${API_URL.cardList}?export=true${columns}`, {
      responseType: 'blob',
      timeout: 20000
    })
      .then((res) => {
        if (res.status !== 200) throw res;

        const fileName = `cards ${moment().format(
          DATE_FORMAT.reportDateTime
        )}.xlsx`;

        saveAs(res.data, fileName);
      })
      .catch(() => {
        dispatch(
          setErrorInfo({
            errorMsg:
              'Error downloading the report. Try again later!'
          })
        );
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      <Helmet>
        <title>Cards | Retrack FMS</title>
      </Helmet>
      <Box
        className="transaction-style-page transaction-style-duplicate-header"
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* -------- Header -------- */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 3,
              py: 2,
              mb: 2,
              flexShrink: 0
            }}
          >
            {/* Left */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#111827'
                }}
              >
                CARDS
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Manage fuel cards, limits and configurations.
              </Typography>
            </Box>

            {/* Right */}
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', md: 'flex-start' },
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap',
                width: '100%'
              }}
            >
              <Box
                sx={{
                  flex: '0 0 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1
                }}
              >
                <ColumnSearch
                  fields={[
                    { key: 'uid', label: 'Card UID', defaultChecked: true },
                    { key: 'vehicle_number', label: 'Vehicle Number' },
                    { key: 'card_category', label: 'Card Category' }
                  ]}
                  values={searchFilters}
                  onApply={(values) => { setSearchFilters(values); handleSearch(''); }}
                  onClear={() => { setSearchFilters({}); handleSearch(''); }}
                  containerSx={{
                    width: { xs: '100%', sm: 320 },
                    flexShrink: 0
                  }}
                  sx={{ flex: { xs: 1, sm: '0 1 320px' } }}
                />
                <ListRefreshButton onRefresh={() => setRefreshKey((value) => value + 1)} />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'stretch', md: 'flex-start' },
                  justifyContent: 'flex-end',
                  gap: 2,
                  flexWrap: 'wrap'
                }}
              >
                {isAdmin && (
                  <FilterSelectInput
                    label="Site"
                    name="site"
                    value={siteFilter}
                    data={siteOptions}
                    onInputChange={handleSiteChange}
                    sx={{ width: { xs: '100%', sm: 220, md: 180 }, mr: 0 }}
                  />
                )}

                <TableColumnVisibilityMenu
                  columns={cardTableHeaders}
                  hiddenColumnIds={cardColumnPreferences.hiddenColumnIds}
                  onToggleColumn={cardColumnPreferences.onToggleColumn}
                  buttonLabel="Columns"
                />

                <Button
                  variant="contained"
                  startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                  onClick={() => setShowExportColumns(true)}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  {isLoading ? 'Exporting...' : 'Export'}
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    height: 40,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Add Card
                </Button>
              </Box>
            </Box>
          </Box>

          {/* -------- Add Card Modal -------- */}
          <NewCard
            open={openModal}
            handleClose={() => setOpenModal(false)}
          />

          <NewCard
            key={editingCard?.id || 'edit-card'}
            open={Boolean(editingCard)}
            initialValues={editingCard}
            handleClose={handleCloseEditCard}
          />

          <ConfirmationDialog
            open={showExportConfirm}
            info="Are you sure you want to export cards report?"
            actionName="Export"
            onClose={() => setShowExportConfirm(false)}
            onSubmit={handleExport}
          />

          <ExportColumnSelector
            open={showExportColumns}
            columns={EXPORT_COLUMNS.cards}
            onClose={() => setShowExportColumns(false)}
            onConfirm={(columns) => {
              setExportColumns(columns);
              setShowExportColumns(false);
              setShowExportConfirm(true);
            }}
          />

          {/* -------- Table -------- */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <CardListTable
              refreshData={refreshKey || location?.state?.fromDashboard}
              searchText={searchText}
              searchFilters={searchFilters}
              siteFilter={siteFilter}
              columnPreferences={cardColumnPreferences}
              onEditCard={handleEditCard}
            />
          </Box>
        </Container>
      </Box>
    </>
  );
};

Cards.propTypes = {
  location: PropTypes.object
};

export default Cards;
